/**
 * Office.js context helpers — reads current email details from Outlook.
 * Falls back to mock data in browser dev mode.
 */

export interface EmailContext {
  subject: string;
  from: string;
  to: string[];
  cc: string[];
  dateCreated: string;
  allAddresses: string[];
  body: string;
  isCompose: boolean;
}

/** Get the email body text from Office.js */
function getEmailBody(item: any): Promise<string> {
  return new Promise((resolve) => {
    if (item.body?.getAsync) {
      item.body.getAsync('text', (result: any) => {
        if (result.status === 'succeeded') {
          resolve(result.value || '');
        } else {
          resolve('');
        }
      });
    } else {
      resolve('');
    }
  });
}

/** Get a property asynchronously (compose mode uses getAsync for to/cc/subject) */
function getAsyncProp(item: any, prop: string): Promise<any> {
  return new Promise((resolve) => {
    if (item[prop]?.getAsync) {
      item[prop].getAsync((result: any) => {
        resolve(result.status === 'succeeded' ? result.value : null);
      });
    } else {
      resolve(item[prop]);
    }
  });
}

/** Detect if we're in compose mode */
function isComposeMode(): boolean {
  if (typeof Office === 'undefined' || !Office.context?.mailbox?.item) return false;
  const item = Office.context.mailbox.item;
  // Compose mode items have getAsync on subject/to/cc; read mode has direct values
  return typeof item.subject?.getAsync === 'function';
}

/**
 * Resolve the loggable participant addresses + the external correspondent,
 * given the raw from/to/cc and the mailbox owner's own address.
 *
 * The contact to log against is the OTHER party, never the mailbox owner:
 *  - received mail: the sender (`from`), unless `from` is the owner
 *  - sent / reply / compose: the first recipient who isn't the owner
 *
 * `allAddresses` excludes the owner so the owner is never matched as the CW
 * contact — defense-in-depth independent of the member-email cache (which can
 * silently fail to load). Deterministic, no Office.js dependency → unit-testable.
 */
export function resolveCorrespondent(
  from: string,
  to: string[],
  cc: string[],
  self: string,
): { correspondent: string; allAddresses: string[] } {
  const norm = (s: string) => (s || '').toLowerCase().trim();
  const selfN = norm(self);
  const isSelf = (a: string) => !!selfN && norm(a) === selfN;

  const fromIsSelf = !from || isSelf(from);
  // received → the sender is the correspondent; sent/reply/compose → first non-self recipient
  const correspondent = fromIsSelf
    ? (to.find((a) => a && !isSelf(a)) || cc.find((a) => a && !isSelf(a)) || '')
    : from;

  // match candidates: every participant EXCEPT the mailbox owner (deduped, order-preserving)
  const seen = new Set<string>();
  const allAddresses: string[] = [];
  for (const a of [from, ...to, ...cc]) {
    const n = norm(a);
    if (!n || isSelf(a) || seen.has(n)) continue;
    seen.add(n);
    allAddresses.push(a);
  }
  return { correspondent, allAddresses };
}

/** An EX/X500 (directory) address is NOT SMTP and never matches CW's stored emails. */
export function isNonSmtpAddress(a: string): boolean {
  return !!a && (!a.includes('@') || a.trim().startsWith('/'));
}

/**
 * b3 fix (Coeur d'Alene "No CW contacts matched", 2026-06-19) — resolve the
 * message's participants to SMTP via Outlook REST.
 *
 * GAL-resolved recipients (directory/Exchange contacts) arrive in
 * `item.to[].emailAddress` as an EX/X500 address ("/o=ExchangeLabs/..."), NOT
 * their SMTP address — so they never match CW's stored SMTP contacts (the
 * exact-match in cw-api.ts matchEmails). `getCallbackTokenAsync({isRest:true})`
 * stays inside the add-in's existing `ReadWriteItem` permission (no Graph scope,
 * no manifest change → no M365 re-deploy) and fetches the server-side message,
 * whose `from`/`toRecipients`/`ccRecipients` ARE SMTP. Returns the SMTP
 * from/to/cc, or `null` on any failure so the caller keeps the original
 * Office.js addresses (degrade-safe — no regression on inbound matching).
 */
function resolveParticipantsSmtpViaRest(): Promise<{ from: string; to: string[]; cc: string[] } | null> {
  return new Promise((resolve) => {
    try {
      const mailbox: any = (typeof Office !== 'undefined' && Office.context?.mailbox) || null;
      const item: any = mailbox?.item;
      if (!mailbox?.getCallbackTokenAsync || !mailbox?.restUrl || !item?.itemId) {
        resolve(null);
        return;
      }
      mailbox.getCallbackTokenAsync({ isRest: true }, (tokenResult: any) => {
        if (tokenResult?.status !== 'succeeded' || !tokenResult.value) {
          resolve(null);
          return;
        }
        let restId: string = item.itemId;
        try {
          restId = mailbox.convertToRestId(item.itemId, Office.MailboxEnums.RestVersion.v2_0);
        } catch {
          /* fall back to the raw itemId */
        }
        const url = `${mailbox.restUrl}/v2.0/me/messages/${restId}?$select=from,toRecipients,ccRecipients`;
        fetch(url, {
          headers: { Authorization: `Bearer ${tokenResult.value}`, Accept: 'application/json' },
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((msg: any) => {
            if (!msg) {
              resolve(null);
              return;
            }
            const addrOf = (rcpt: any): string => rcpt?.emailAddress?.address || '';
            resolve({
              from: addrOf(msg.from),
              to: (msg.toRecipients || []).map(addrOf).filter(Boolean),
              cc: (msg.ccRecipients || []).map(addrOf).filter(Boolean),
            });
          })
          .catch(() => resolve(null));
      });
    } catch {
      resolve(null);
    }
  });
}

/** Get the current email context from Outlook */
export function getEmailContext(): Promise<EmailContext> {
  return new Promise(async (resolve) => {
    if (typeof Office !== 'undefined' && Office.context?.mailbox?.item) {
      const item = Office.context.mailbox.item;
      const compose = isComposeMode();

      let from: string;
      let to: string[];
      let cc: string[];
      let subject: string;

      if (compose) {
        // Compose mode — properties are async
        subject = (await getAsyncProp(item, 'subject')) || '(No Subject)';
        const toRecipients = (await getAsyncProp(item, 'to')) || [];
        const ccRecipients = (await getAsyncProp(item, 'cc')) || [];
        // Prefer the message's actual sender when present (read items always expose
        // item.from directly, even if compose-detection misfired); only fall back to
        // the mailbox owner in TRUE compose, where the owner is genuinely the sender.
        from = item.from?.emailAddress || Office.context.mailbox.userProfile?.emailAddress || '';
        to = toRecipients.map?.((r: any) => r.emailAddress || r) || [];
        cc = ccRecipients.map?.((r: any) => r.emailAddress || r) || [];
      } else {
        // Read mode — properties are direct
        from = item.from?.emailAddress || '';
        to = (item.to || []).map((r: { emailAddress: string }) => r.emailAddress);
        cc = (item.cc || []).map((r: { emailAddress: string }) => r.emailAddress);
        subject = item.subject || '(No Subject)';
      }

      // b3: if a read-mode message carries any EX/X500 (directory) address, re-fetch
      // the server-side message via Outlook REST so participants are SMTP — otherwise
      // GAL-resolved contacts (e.g. the Coeur d'Alene Tribe recipients) never match
      // CW's stored SMTP emails. Done BEFORE resolveCorrespondent so BOTH the
      // correspondent (the display + match key) and allAddresses come out as SMTP.
      // Degrade-safe: on any failure the helper returns null and we keep the original
      // Office.js addresses, so inbound-sender matching is never regressed.
      if (!compose && [from, ...to, ...cc].some(isNonSmtpAddress)) {
        const smtp = await resolveParticipantsSmtpViaRest();
        if (smtp) {
          from = smtp.from || from;
          to = smtp.to.length ? smtp.to : to;
          cc = smtp.cc.length ? smtp.cc : cc;
        }
      }

      // Exclude the mailbox owner from match candidates so the add-in never logs
      // against the user themselves (Bug: read-as-compose misdetect / reply mode).
      const self = Office.context?.mailbox?.userProfile?.emailAddress || '';
      const { correspondent, allAddresses } = resolveCorrespondent(from, to, cc, self);
      const body = await getEmailBody(item);

      resolve({
        subject,
        // surface the actual correspondent (sender for received, recipient for sent)
        // so display + auto-match key on the other party, never the mailbox owner.
        from: correspondent || from,
        to,
        cc,
        dateCreated: item.dateTimeCreated?.toISOString() || new Date().toISOString(),
        allAddresses,
        body,
        isCompose: compose,
      });
    } else {
      // Dev/test mode — use URL params or defaults
      const params = new URLSearchParams(window.location.search);
      const from = params.get('from') || 'jeff_layton@yahoo.com';
      const to = (params.get('to') || 'gary@bluwave.net').split(',');
      const subject = params.get('subject') || '[Dev Mode] Q2 Renewal Discussion';
      resolve({
        subject,
        from,
        to,
        cc: [],
        dateCreated: new Date().toISOString(),
        allAddresses: [from, ...to].filter(Boolean),
        body: params.get('body') || 'This is a mock email body for development testing.\n\nBest regards,\nJeff',
        isCompose: params.get('compose') === 'true',
      });
    }
  });
}
